/**
 * Document Intelligence — Upload Document.
 *
 * Pick one PDF (or scanned image), press Process, and watch it run: the file is
 * read by a document-analysis model, classified against the active categories in
 * Documents Master, and its fields extracted. Progress is streamed by the
 * backend per stage, so each row below reports what is actually happening
 * rather than a spinner that means "something".
 *
 * The finished result is handed to the result screen through router state. It
 * is not stored anywhere yet — see the backend service — so a reload loses it;
 * the result screen says so rather than showing an empty page.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'sharedUi/Button';
import { Toast } from 'sharedUi/Toast';
import PermissionGuard from '../../components/guards/PermissionGuard';
import PageHeader from '../../components/common/page-header';
import FileDropzone from '../../components/common/file-dropzone';
import FormSelect from '../../components/forms/form-select';
import { Icon } from '../../components/common/icon';
import { JOB_NAMES } from '../../constants/jobNames';
import { aiModelsService } from '../../services/ai-models.service';
import { documentProcessingService } from '../../services/document-processing.service';
import { extractErrorMessage } from '../../utils/error-handler';
import {
  DEFAULT_DOCUMENT_LANGUAGE,
  DOCUMENT_LANGUAGES,
  type DocumentLanguage,
  type DocumentProcessingEvent,
  type ProcessingStep,
} from '../../types/document-intelligence.types';
import { DOCUMENT_RESULT_PATH } from './paths';

const ACCEPT = [
  'application/pdf', '.pdf',
  'image/jpeg', '.jpg', '.jpeg',
  'image/png', '.png',
  'image/tiff', '.tif', '.tiff',
];
const ACCEPT_LABEL = 'PDF, JPG, PNG or TIFF';
const MAX_SIZE_MB = 50;

const STEP_LABEL: Record<ProcessingStep, string> = {
  read: 'Reading the document',
  classify: 'Identifying the document type',
  extract: 'Extracting the fields',
  translate: 'Translating into English',
};
const STEP_ORDER: ProcessingStep[] = ['read', 'classify', 'extract'];

/** The steps a document in `language` goes through — the backend's
 *  steps_for(): anything not English is translated at the end. */
const stepsFor = (language: DocumentLanguage): ProcessingStep[] =>
  (language === DEFAULT_DOCUMENT_LANGUAGE ? STEP_ORDER : [...STEP_ORDER, 'translate']);

type StepState = 'pending' | 'running' | 'done' | 'failed';

interface StepRow {
  state: StepState;
  detail?: string;
}

const EMPTY_STEPS: Record<ProcessingStep, StepRow> = {
  read: { state: 'pending' },
  classify: { state: 'pending' },
  extract: { state: 'pending' },
  translate: { state: 'pending' },
};

const StepIcon = ({ state }: { state: StepState }) => {
  if (state === 'running') {
    // A ring with one coloured quarter, spun with Tailwind's own animation
    // rather than PrimeIcons' `pi-spin`: that one is switched off by the
    // browser when the operating system asks for reduced motion, which leaves
    // a spinner that never turns — indistinguishable from a hung step.
    return (
      <span
        className="h-[18px] w-[18px] animate-spin rounded-full border-2 border-line border-t-primary"
        aria-hidden
      />
    );
  }
  if (state === 'done') {
    return <span className="text-success"><Icon name="check-circle" size={18} /></span>;
  }
  if (state === 'failed') {
    return <span className="text-error"><Icon name="x-circle" size={18} /></span>;
  }
  return <span className="h-[18px] w-[18px] rounded-full border-2 border-line" aria-hidden />;
};

const UploadDocument = () => {
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [steps, setSteps] = useState<Record<ProcessingStep, StepRow>>(EMPTY_STEPS);
  const [failure, setFailure] = useState<string | null>(null);
  const [ocrModel, setOcrModel] = useState('');
  const [llmModel, setLlmModel] = useState('');
  const [language, setLanguage] = useState<DocumentLanguage>(DEFAULT_DOCUMENT_LANGUAGE);

  // Stop a run that is still going when the page is left.
  useEffect(() => () => abortRef.current?.abort(), []);

  const { data: ocrModels } = useQuery({
    queryKey: ['ai-models', 'document_analysis', 'ready'],
    // ready_only: an OCR model with no credentials fails on the first page it
    // is sent, so it is not offered at all.
    queryFn: () => aiModelsService.list({
      page: 1, page_size: 100, model_type: 'document_analysis', ready_only: true,
    }),
    staleTime: 5 * 60 * 1000,
  });
  const { data: llmModels } = useQuery({
    queryKey: ['ai-models', 'runnable', 'text', 'ready'],
    // Only what this pipeline can use: models that take a text prompt
    // (text_only drops speech, transcription and detection models, which are
    // also typed "base") and that are active with working credentials.
    queryFn: () => aiModelsService.list({
      page: 1, page_size: 100, runnable_only: true, text_only: true, ready_only: true,
    }),
    staleTime: 5 * 60 * 1000,
  });

  const toOptions = (rows?: { _id: string; displayName: string; modelId: string; isDefault?: boolean }[]) =>
    (rows || []).map((m) => ({
      value: m._id,
      label: `${m.displayName}${m.isDefault ? ' (default)' : ''}`,
    }));

  const handleEvent = (event: DocumentProcessingEvent) => {
    const step = event.step as ProcessingStep;
    if (!stepsFor(language).includes(step)) return;
    setSteps((prev) => ({
      ...prev,
      [step]: {
        state: event.status === 'started' ? 'running'
          : event.status === 'done' ? 'done' : 'failed',
        detail: event.status === 'failed' ? event.error : detailFor(event),
      },
    }));
  };

  const detailFor = (event: DocumentProcessingEvent): string | undefined => {
    if (event.step === 'read' && event.status === 'done') {
      return `${event.pageCount} page(s), ${event.characters?.toLocaleString()} characters`;
    }
    if (event.step === 'classify' && event.status === 'done') {
      const pct = Math.round((event.confidence ?? 0) * 100);
      return `${event.category} · ${pct}% confidence${event.reason ? ` · ${event.reason}` : ''}`;
    }
    if (event.step === 'translate' && event.status === 'done') {
      return event.warning
        ? 'Translation unavailable — the original values are kept'
        : `${event.translated ?? 0} field(s) translated`;
    }
    if (event.step === 'extract' && event.status === 'done') {
      return `${event.fieldCount} field(s)`
        + (event.missingRequired ? ` · ${event.missingRequired} required field(s) not found` : '');
    }
    return event.message;
  };

  const handleProcess = async () => {
    const file = files[0];
    if (!file) return;
    setProcessing(true);
    setFailure(null);
    setSteps(EMPTY_STEPS);
    abortRef.current = new AbortController();

    try {
      const result = await documentProcessingService.process(file, {
        ocrModelRef: ocrModel || null,
        llmModelRef: llmModel || null,
        language,
        onEvent: handleEvent,
        signal: abortRef.current.signal,
      });
      // Nothing has been stored: the result lives in this response alone, so it
      // travels to the result screen in router state. The operator decides
      // there whether it is worth keeping, and the file goes with it because
      // saving needs to upload it again.
      navigate(DOCUMENT_RESULT_PATH, { state: { result, file } });
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') return;
      const message = extractErrorMessage(error, 'Processing failed.');
      setFailure(message);
      setSteps((prev) => {
        // Whichever stage was running is the one that failed.
        const running = stepsFor(language).find((s) => prev[s].state === 'running');
        return running ? { ...prev, [running]: { state: 'failed', detail: message } } : prev;
      });
      toast.current?.show({ severity: 'error', summary: 'Processing failed', detail: message, life: 10000 });
    } finally {
      setProcessing(false);
      abortRef.current = null;
    }
  };

  return (
    <PermissionGuard jobName={JOB_NAMES.AI_MODEL_MASTER} resourceName="Document Intelligence">
      <Toast ref={toast} position="top-right" />
      <div className="p-3" data-testid="SCR-DocumentIntelligence-Upload">
        <PageHeader
          icon="pi pi-upload"
          title="Upload Document"
          subtitle="Read a document, identify its type, and extract its data"
          testId="UploadDocument.Header"
        />
        <div className="flex justify-center py-8">
          <div className="w-full max-w-2xl">
            <FileDropzone
              files={files}
              onFilesChange={(next) => { setFiles(next); setSteps(EMPTY_STEPS); setFailure(null); }}
              accept={ACCEPT}
              acceptLabel={ACCEPT_LABEL}
              maxSizeMb={MAX_SIZE_MB}
              multiple={false}
              disabled={processing}
              testId="UploadDocument.Dropzone"
            />

            {/* Language, then models. The models left blank use the project's
                defaults; both are shown because no default is set for either
                kind today. Language always has a value — English unless changed. */}
            {files.length > 0 && !processing && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <FormSelect
                  name="language" label="Language" value={language}
                  options={DOCUMENT_LANGUAGES}
                  onChange={(e) => setLanguage((e.target.value || DEFAULT_DOCUMENT_LANGUAGE) as DocumentLanguage)}
                />
                <FormSelect
                  name="ocrModel" label="Reader (OCR model)" value={ocrModel}
                  options={toOptions(ocrModels?.data)} showClear
                  placeholder={ocrModels && !ocrModels.data?.length
                    ? 'No configured OCR model' : 'Project default'}
                  onChange={(e) => setOcrModel(e.target.value)}
                />
                <FormSelect
                  name="llmModel" label="AI model" value={llmModel}
                  options={toOptions(llmModels?.data)} showClear
                  placeholder={llmModels && !llmModels.data?.length
                    ? 'No configured AI model' : 'Project default'}
                  onChange={(e) => setLlmModel(e.target.value)}
                />
              </div>
            )}

            {/* Progress */}
            {(processing || failure || steps.read.state !== 'pending') && (
              <div className="mt-4 rounded-lg border border-line bg-surface p-4"
                data-testid="UploadDocument.Progress" aria-live="polite">
                <ul className="flex flex-col gap-3">
                  {stepsFor(language).map((step) => (
                    <li key={step} className="flex items-start gap-3"
                      data-testid={`UploadDocument.Progress.${step}`}>
                      <span className="mt-0.5 flex h-[18px] w-[18px] items-center justify-center">
                        <StepIcon state={steps[step].state} />
                      </span>
                      <div className="min-w-0">
                        <p className={`text-body ${steps[step].state === 'pending'
                          ? 'text-ink-muted' : 'text-ink font-medium'}`}>
                          {STEP_LABEL[step]}
                        </p>
                        {steps[step].detail && (
                          <p className={`text-body-sm ${steps[step].state === 'failed'
                            ? 'text-error' : 'text-ink-muted'}`}>
                            {steps[step].detail}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                {processing && (
                  <p className="mt-3 text-caption text-ink-muted">
                    This can take a minute for a long or scanned document. Leaving the page stops it.
                  </p>
                )}
              </div>
            )}

            {files.length > 0 && (
              <div className="mt-4 flex justify-end gap-2">
                <Button
                  label={processing ? 'Processing...' : 'Process'}
                  icon={processing ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
                  onClick={handleProcess}
                  disabled={processing}
                  testId="UploadDocument.Button.Process"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default UploadDocument;
